import FollowerRange from '@gp/components/FollowerRange';
import GeneralModal from '@gp/components/GeneralModal';
import GRActionItemsFormControl from '@gp/components/GRActionItemsFormControl';
import GPSelect from '@gp/components/Select';
import TinyEditor from '@gp/components/TinyEditor';
import {
  CONSTANT_TEXTS,
  FIELD_COMMON_ERR_MSG,
  MILESTONE_CATEGORY_LIST_OPTIONS,
  SELECT_PANEL_HEIGHT_WITHOUT_SEARCH_BOX,
  STATIC_GATEREVIEW_DECISIONS,
  UPLOAD_FOLDER_NAME_MAP } from '@gp/constants/const';
import { MilestoneDateStatus } from '@gp/enums/milestone';
import { EDIT_PAGE_KEY_MAP, useAutoSaveParams } from '@gp/hooks/useAutoSaveParams';
import TBCDatePicker from '@gp/pages/Pipeline/ProjectDetails/Milestone/components/TBCDatePicker';
import FinancialsFormControl from '@gp/pages/Pipeline/ProjectDetails/Milestone/components/UpdateGRModal/FinancialsFormControl';
import {
  getInitialKeyMilestonesFinancials,
  getInitialKeyMilestonesFinancialsForecast,
} from '@gp/pages/Pipeline/ProjectDetails/Milestone/components/UpdateGRModal/FinancialsFormControl/utils';
import '@gp/pages/Pipeline/ProjectDetails/Milestone/components/UpdateGRModal/index.scss';
import UploadFileList from '@gp/pages/Pipeline/ProjectDetails/Milestone/components/UpdateGRModal/UploadFileList';
import { MilestoneDateTips } from '@gp/pages/Pipeline/ProjectDetails/Milestone/constants/elements';
import type { IGRAction, IMilestone } from '@gp/pages/Pipeline/ProjectDetails/Milestone/types';
import {
  enhanceMilestoneFieldChangeHandler,
  formatMilestoneOptions,
  getIsNotEmptyList,
} from '@gp/pages/Pipeline/ProjectDetails/Milestone/utils';
import type { IMaterialVO } from '@gp/types/global';
import type { IMilestoneFinancialForecastVO, IMilestoneFinancialVO } from '@gp/types/milestones';
import {
  formatComposedTeaFormItemMessage,
  formatComposedTeaFormItemStatus,
  formatTeaFormItemMessage,
  formatTeaFormItemStatus,
} from '@gp/utils/common';
import TipContext from '@home/components/TipContext';
import type { IBaseProps } from '@home/types/common';
import { isEmptyValue } from '@home/utils/common';
import { useMemoizedFn } from 'ahooks';
import classnames from 'classnames';
import { isEqual } from 'lodash-es';
import React, { useEffect, useImperativeHandle, useMemo, useRef, useState } from 'react';
import { useField, useForm } from 'react-final-form-hooks';
import { Col, Form, FormItem, Row } from 'tea-component';

export interface IUpdateGRFormModel {
  gate_review: string;
  gate_review_decision: string;
  dev_cost_actual: string;
  budget_next_milestone: string;
  approved_budget_to_next: string;
  dev_cost: string;
  key_milestones_financial: IMilestoneFinancialVO[];
  key_milestones_financial_forecast: IMilestoneFinancialForecastVO[];
  key_feedback: string;
  action_items: IGRAction[];
  next_milestone_scope: string;
  material: IMaterialVO[];
}

interface IUpdateGRModalProps extends IBaseProps {
  activeMilestone?: string;
  currentMilestoneDate?: string;
  // Milestones集合
  milestones?: IMilestone[];
  separatedMilestones?: IMilestone[];
  // 弹窗是否可见
  visible?: boolean;
  // 弹窗是否可见的setState
  setVisible?: React.Dispatch<React.SetStateAction<boolean>>;
  // confirm的回调
  onConfirm?: (payload: IUpdateGRFormModel) => void;
  // cancel的回调
  onCancel?: (payload: IUpdateGRFormModel) => void;
}

export interface IUpdateGRModalInstance {
  init: (payload: Partial<IUpdateGRFormModel>, currentMilestoneDate?: string) => void;
}

const DEFAULT_FORM_MODEL: IUpdateGRFormModel = {
  gate_review: '',
  gate_review_decision: '',
  dev_cost_actual: '',
  budget_next_milestone: '',
  approved_budget_to_next: '',
  dev_cost: '',
  key_milestones_financial: [],
  key_milestones_financial_forecast: [],
  key_feedback: '',
  action_items: [],
  next_milestone_scope: '',
  material: [],
  milestone: '',
  category: '',
  date: '',
  date_confirm: MilestoneDateStatus.Confirm,
};

const handleValidate = (values?: IUpdateGRFormModel): Record<string, string> => {
  if (!values) return { milestone: FIELD_COMMON_ERR_MSG, date: FIELD_COMMON_ERR_MSG };
  const errors: Record<string, string> = {};
  const actionItemsValue = values?.action_items;
  const keyMilestonesFinancialsValue = values?.key_milestones_financial;
  const isKeyMilestonesFinancialsValid =
    !Array.isArray(keyMilestonesFinancialsValue) ||
    !keyMilestonesFinancialsValue.length ||
    keyMilestonesFinancialsValue?.every(
      (keyMilestoneFinancial) => !isEmptyValue(keyMilestoneFinancial?.milestone),
    );
  const isActionItemsValid =
    !Array.isArray(actionItemsValue) ||
    !actionItemsValue.length ||
    actionItemsValue?.every((actionItem) => !isEmptyValue(actionItem?.due_date));
  if (!isKeyMilestonesFinancialsValid) {
    errors.key_milestones_financial = FIELD_COMMON_ERR_MSG;
  }
  if (!isActionItemsValid) {
    errors.action_items = FIELD_COMMON_ERR_MSG;
  }
  return errors;
};

const getIsFormAvailable = (values?: IUpdateGRFormModel): boolean => {
  const {
    gate_review: gateReview,
    gate_review_decision: gateReviewDecision,
    dev_cost_actual: devCostActual,
    budget_next_milestone: budgetNextMilestone,
    approved_budget_to_next: approvedBudgetToNext,
    dev_cost: devCost,
    key_milestones_financial: keyMilestonesFinancials,
    key_milestones_financial_forecast: keyMilestonesFinancialsForecast,
    key_feedback: keyFeedback,
    action_items: actionItems,
    next_milestone_scope: nextMilestoneScope,
    material: materials,
  } = values || {};
  return (
    !!gateReview ||
    !!gateReviewDecision ||
    !!devCostActual ||
    !!budgetNextMilestone ||
    !!approvedBudgetToNext ||
    !!devCost ||
    !!getIsNotEmptyList(keyMilestonesFinancials) ||
    !!getIsNotEmptyList(keyMilestonesFinancialsForecast) ||
    !!keyFeedback ||
    getIsNotEmptyList(actionItems, ['id']) ||
    !!nextMilestoneScope ||
    getIsNotEmptyList(materials)
  );
};

const UpdateGRModal: React.ForwardRefRenderFunction<IUpdateGRModalInstance, IUpdateGRModalProps> = (
  props,
  ref,
) => {
  const {
    style,
    className,
    activeMilestone,
    currentMilestoneDate,
    separatedMilestones,
    milestone,
    visible = false,
    isUseSaveDatas = false,
    setVisible,
    onConfirm,
    onCancel,
    milestones,
    milestoneCategory,
    actionItemCategory,
  } = props;

  const milestoneOptions = useMemo(() => {
    return formatMilestoneOptions(milestones);
  }, [milestones]);

  const [initialFileDatas, setInitialFileDatas] = useState<IMaterialVO[]>([]);

  const [submitted, setSubmitted] = useState(false);

  const handleConfirm = useMemoizedFn(async (values: IUpdateGRFormModel) => {
    await onConfirm?.(values);
    hasChangedRef.current = false;
    setSubmitted(false);
    form?.restart();
  });

  const handleCancel = useMemoizedFn(() => {
    setVisible?.(false);
    setSubmitted(false);
    hasChangedRef.current = false;
    form?.restart();
    onCancel?.(values);
  });

  const { form, values, submitting, handleSubmit } = useForm({
    initialValues: DEFAULT_FORM_MODEL,
    onSubmit: handleConfirm,
    validate: handleValidate,
  });

  const initialValueRef = useRef(values);
  const hasChangedRef = useRef(false);

  useEffect(() => {
    if (values !== initialValueRef.current) {
      hasChangedRef.current = true;
    }
  }, [values]);

  const gateReviewField = useField('gate_review', form);
  const gateReviewDecisionField = useField('gate_review_decision', form);
  const milestoneField = useField('milestone', form);
  const categoryField = useField('category', form);
  const dateField = useField('date', form);
  const dateConfirmField = useField('date_confirm', form);
  const devCostField = useField('dev_cost', form);
  const keyMilestonesFinancialsField = useField('key_milestones_financial', form);
  const keyMilestonesFinancialsForecastField = useField('key_milestones_financial_forecast', form);
  const keyFeedbackField = useField('key_feedback', form);
  const actionItemsField = useField('action_items', form);
  const nextMilestoneScopeField = useField('next_milestone_scope', form);
  const materialsField = useField('material', form);

  const formAvailable = useMemo<boolean>(() => getIsFormAvailable(values), [values]);

  const onMilestoneChange = useMemo<(payload: string) => void>(() => {
    return enhanceMilestoneFieldChangeHandler(milestoneField, categoryField, milestoneCategory);
  }, [milestoneField, categoryField, milestoneCategory]);

  const disabledCategory = useMemo(() => {
    const key = values?.milestone || '';
    const hasKey = milestoneCategory ? Object.keys(milestoneCategory).includes(key) : false;
    return hasKey || !key;
  }, [values, milestoneCategory]);

  const pristine = useMemo<boolean>(() => {
    const commonsPristine = [
      gateReviewField,
      gateReviewDecisionField,
      devCostField,
      keyFeedbackField,
      nextMilestoneScopeField,
      milestoneField,
      dateField,
      dateConfirmField,
      categoryField,
    ].every((field) => !!field?.meta?.pristine);

    const specialsPristine = [
      { key: 'key_milestones_financial', field: keyMilestonesFinancialsField },
      {
        key: 'key_milestones_financial_forecast',
        field: keyMilestonesFinancialsForecastField,
      },
      { key: 'action_items', field: actionItemsField },
      { key: 'material', field: materialsField },
    ].every((meta) => {
      const { key, field } = meta || {};
      return isEqual(values?.[key], field?.meta?.initial);
    });

    return commonsPristine && specialsPristine;
  }, [
    gateReviewField,
    gateReviewDecisionField,
    devCostField,
    keyFeedbackField,
    nextMilestoneScopeField,
    keyMilestonesFinancialsField,
    keyMilestonesFinancialsForecastField,
    actionItemsField,
    materialsField,
    values,
  ]);

  const handleSubmitInPage = useMemoizedFn(() => {
    setSubmitted(true);
    handleSubmit?.();
  });

  useImperativeHandle(ref, () => ({
    init: (payload, currentMilestoneDate) => {
      const initialKeyMilestonesFinancialsValue = getInitialKeyMilestonesFinancials(
        payload?.key_milestones_financial,
        currentMilestoneDate,
        separatedMilestones,
        true,
      );
      const initialKeyMilestonesFinancialsForecastValue = getInitialKeyMilestonesFinancialsForecast(
        payload?.key_milestones_financial_forecast,
      );
      form?.initialize({
        ...DEFAULT_FORM_MODEL,
        ...payload,
        key_milestones_financial: initialKeyMilestonesFinancialsValue,
        key_milestones_financial_forecast: initialKeyMilestonesFinancialsForecastValue,
      });
      setInitialFileDatas(payload?.material || []);
    },
  }));

  useEffect(() => {
    if (!visible) return;
    const {
      milestone: milestoneName,
      date: milestoneDate,
      date_confirm: dateConfirm,
      category,
    } = milestone || {};
    const valueTemp = {
      ...values,
      milestone: milestoneName || '',
      category: category || milestoneCategory?.[milestoneName || ''] || '',
      date: milestoneDate || '',
      date_confirm: dateConfirm || MilestoneDateStatus.Confirm,
    };
    form?.initialize(valueTemp);
  }, [visible, milestone]);

  const pageKeyCur = useMemo(() => {
    return `${EDIT_PAGE_KEY_MAP.pipeline_gameDetails_milestone_editGR}_${milestone?.project_id}_${milestone?.milestone_id}`;
  }, [milestone]);

  const titleTypeCur = useMemo(() => {
    const { category } = milestone || {};
    return category === 'gr' ? 'Edit GR' : 'Edit Milestone';
  }, [milestone]);

  const getSaveParamsCur = () => {
    return {
      ...milestone,
      ...values,
    };
  };

  // 每隔5秒自动保存
  useAutoSaveParams({
    pageKey: pageKeyCur,
    initialParams: milestone,
    saveParamsCur: getSaveParamsCur(),
    isBegin: visible,
    isChangeValue: hasChangedRef.current,
  });

  return (
    <GeneralModal
      style={style}
      className={classnames('dbg-milestone-gr-edit-modal', className)}
      title={activeMilestone ? `${titleTypeCur} (${activeMilestone})` : titleTypeCur}
      // modalSize="m"
      modalSize="auto"
      isFont14={true}
      disabledConfirm={isUseSaveDatas ? false : !formAvailable || pristine}
      loadingConfirm={submitting}
      visible={visible}
      onConfirm={handleSubmitInPage}
      onClose={handleCancel}
      content={
        <div className="dbg-milestone-gr-edit font14">
          <Form className="gp-form dbg-milestone-gr-edit-form" layout="fixed" fixedLabelWidth="160">
            <Row>
              <Col span={24}>
                <FormItem
                  required
                  label={CONSTANT_TEXTS.MILESTONE}
                  showStatusIcon={false}
                  status={formatTeaFormItemStatus(submitted, gateReviewField?.meta)}
                  message={formatTeaFormItemMessage(submitted, gateReviewField?.meta)}
                >
                  <div className="dbg-team-game-information-edit-modal-content-form-Studio">
                    <div className="dbg-team-game-information-edit-modal-content-form-Studio-left">
                      <FollowerRange
                        {...milestoneField?.input}
                        placeholder="Please select Milestone"
                        customizePlaceholder="Please input Milestone"
                        options={milestoneOptions}
                        onChange={onMilestoneChange}
                      />
                    </div>
                    <div className="dbg-update-edit-divider">-</div>
                    <div className="dbg-team-game-information-edit-modal-content-form-Studio-right">
                      <GPSelect
                        {...categoryField?.input}
                        placeholder="Please select Category"
                        clearable={false}
                        options={MILESTONE_CATEGORY_LIST_OPTIONS}
                        disabled={disabledCategory}
                      />
                    </div>
                  </div>
                </FormItem>
              </Col>
            </Row>
            <Row>
              <Col span={24}>
                <FormItem
                  required
                  label={
                    <>
                      {CONSTANT_TEXTS.MILESTONE_TIME}
                      <TipContext
                        className="dbg-gr-edit-tips"
                        placement="right"
                        AWidth={555}
                        tipText={<MilestoneDateTips />}
                      />
                    </>
                  }
                  showStatusIcon={false}
                  status={formatTeaFormItemStatus(submitted, dateField?.meta)}
                  message={formatTeaFormItemMessage(submitted, dateField?.meta)}
                >
                  <TBCDatePicker
                    value={{
                      dateConfirm: dateConfirmField?.input?.value,
                      date: dateField?.input?.value,
                    }}
                    onChange={(payload) => {
                      const { dateConfirm, date } = payload || {};
                      dateConfirmField?.input?.onChange(dateConfirm);
                      dateField?.input?.onChange(date);
                    }}
                  />
                </FormItem>
              </Col>
            </Row>
            <Row>
              <Col span={24}>
                <FormItem
                  label="Decision"
                  showStatusIcon={false}
                  status={formatComposedTeaFormItemStatus(submitted, [
                    gateReviewField?.meta,
                    gateReviewDecisionField?.meta,
                  ])}
                  message={formatComposedTeaFormItemMessage(submitted, [
                    gateReviewField?.meta,
                    gateReviewDecisionField?.meta,
                  ])}
                >
                  <div className="dbg-milestone-gr-edit-gate">
                    <GPSelect
                      {...gateReviewField?.input}
                      placeholder="Decision"
                      clearable={false}
                      listHeight={SELECT_PANEL_HEIGHT_WITHOUT_SEARCH_BOX}
                      options={STATIC_GATEREVIEW_DECISIONS}
                    />
                  </div>
                  <div className="dbg-wang-editor-box">
                    <TinyEditor
                      className="gp-word-break-word"
                      placeholder="Please input Decision"
                      status="success"
                      value={gateReviewDecisionField?.input?.value}
                      getEditorContent={(payload?: string) => {
                        gateReviewDecisionField?.input?.onChange(payload || '');
                      }}
                      getEditorText={() => {}}
                      getIsFocused={() => {}}
                    />
                  </div>
                </FormItem>
              </Col>
            </Row>
            <Row>
              <Col span={24}>
                <FormItem label="Financials" className="dbg-milestone-gr-edit-item-fina">
                  <FinancialsFormControl
                    submitted={submitted}
                    currentMilestoneDate={currentMilestoneDate}
                    milestones={separatedMilestones}
                    devCostField={devCostField}
                    keyMilestonesFinancialsField={keyMilestonesFinancialsField}
                    keyMilestonesFinancialsForecastField={keyMilestonesFinancialsForecastField}
                  />
                </FormItem>
              </Col>
            </Row>
            <Row className="dbg-milestone-gr-edit-item-feedback">
              <Col span={24}>
                <FormItem
                  label="Key Feedback"
                  showStatusIcon={false}
                  status={formatTeaFormItemStatus(submitted, keyFeedbackField?.meta)}
                  message={formatTeaFormItemMessage(submitted, keyFeedbackField?.meta)}
                >
                  <div className="dbg-wang-editor-box">
                    <TinyEditor
                      className="gp-word-break-word"
                      placeholder="Please input Key Feedback"
                      status="success"
                      value={keyFeedbackField?.input?.value}
                      getEditorContent={(payload?: string) => {
                        keyFeedbackField?.input?.onChange(payload || '');
                      }}
                      getEditorText={() => {}}
                      getIsFocused={() => {}}
                    />
                  </div>
                </FormItem>
              </Col>
            </Row>
            <Row className="dbg-milestone-gr-edit-item-gr-action-items">
              <Col span={24}>
                <FormItem
                  label="GR Action Items"
                  showStatusIcon={false}
                  status={formatTeaFormItemStatus(submitted, actionItemsField?.meta)}
                  message={formatTeaFormItemMessage(submitted, actionItemsField?.meta)}
                >
                  <GRActionItemsFormControl
                    {...actionItemsField?.input}
                    newFeatureTipsIntersection
                    newFeatureTipsKey="pipeline_project_details_milestone_gr_action_items_select_status"
                    actionItemCategory={actionItemCategory}
                  />
                </FormItem>
              </Col>
            </Row>
            <Row className="dbg-milestone-gr-edit-item-milestone-scope">
              <Col span={24}>
                <FormItem
                  label="Focus of the Next Review"
                  showStatusIcon={false}
                  status={formatTeaFormItemStatus(submitted, nextMilestoneScopeField?.meta)}
                  message={formatTeaFormItemMessage(submitted, nextMilestoneScopeField?.meta)}
                >
                  <div className="dbg-wang-editor-box">
                    <TinyEditor
                      className="gp-word-break-word"
                      placeholder="Please input Focus of the Next Review"
                      status="success"
                      value={nextMilestoneScopeField?.input?.value}
                      getEditorContent={(payload?: string) => {
                        nextMilestoneScopeField?.input?.onChange(payload || '');
                      }}
                      getEditorText={() => {}}
                      getIsFocused={() => {}}
                    />
                  </div>
                </FormItem>
              </Col>
            </Row>
            <Row className="dbg-milestone-gr-edit-item-milestone-documents">
              <Col span={24}>
                <FormItem
                  label="Documents"
                  showStatusIcon={false}
                  status={formatTeaFormItemStatus(submitted, materialsField?.meta)}
                  message={formatTeaFormItemMessage(submitted, materialsField?.meta)}
                >
                  <UploadFileList
                    uniqueId="1"
                    defaultList={initialFileDatas}
                    onResult={(data?: IMaterialVO[]) => {
                      materialsField?.input?.onChange(data || []);
                    }}
                    // 组件内部是否允许提交的回调
                    getIsAllow={(_payload: boolean) => {}}
                    uploadFolderName={UPLOAD_FOLDER_NAME_MAP.milestoneReview}
                  />
                </FormItem>
              </Col>
            </Row>
          </Form>
        </div>
      }
    />
  );
};

export default React.forwardRef(UpdateGRModal);
